import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Settings, Store, Clock, CreditCard, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [shop, setShop] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [shopForm, setShopForm] = useState({ name: "", address: "", phone: "", description: "" });
  const [hours, setHours] = useState({});
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Poll for Stripe payment status
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [shopRes, statsRes] = await Promise.all([
        API.get("/barbershop"),
        API.get("/appointments/stats"),
      ]);
      if (shopRes.data) {
        setShop(shopRes.data);
        setShopForm({
          name: shopRes.data.name || "",
          address: shopRes.data.address || "",
          phone: shopRes.data.phone || "",
          description: shopRes.data.description || "",
        });
        setHours(shopRes.data.working_hours || {});
      }
      setSubscription(statsRes.data?.subscription);
    } catch {}
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Verification du paiement expire. Verifiez votre email.");
      return;
    }
    try {
      const res = await API.get(`/subscription/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        toast.success("Paiement reussi ! Vous etes maintenant Pro.");
        await refreshUser();
        loadData();
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }
      if (res.data.status === "expired") {
        toast.error("Session expiree. Veuillez reessayer.");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const saveShop = async () => {
    setSaving(true);
    try {
      await API.put("/barbershop", { ...shopForm, working_hours: hours });
      toast.success("Informations mises a jour");
      loadData();
    } catch {
      toast.error("Erreur lors de la mise a jour");
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await API.post("/subscription/checkout", {
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur Stripe");
      setUpgrading(false);
    }
  };

  const dayLabels = {
    lundi: "Lundi", mardi: "Mardi", mercredi: "Mercredi",
    jeudi: "Jeudi", vendredi: "Vendredi", samedi: "Samedi", dimanche: "Dimanche"
  };

  const toggleDayClosed = (day) => {
    setHours({ ...hours, [day]: { ...hours[day], closed: !hours[day].closed } });
  };

  const updateHours = (day, field, value) => {
    setHours({ ...hours, [day]: { ...hours[day], [field]: value } });
  };

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl font-bold tracking-tight mb-6">Parametres</h1>

      {/* Subscription Card */}
      <div className="bg-white rounded-xl border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard size={20} className="text-primary" />
          <h2 className="font-semibold text-base">Abonnement</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Badge className={`${subscription?.plan === "pro" ? "bg-primary text-white" : "bg-muted text-foreground"} text-xs border-0`}>
              {subscription?.plan === "pro" ? "Pro" : "Gratuit"}
            </Badge>
            <p className="text-sm text-muted-foreground mt-2">
              {subscription?.plan === "pro"
                ? "Reservations illimitees. Merci de votre confiance !"
                : "20 reservations/mois. Passez a Pro pour des reservations illimitees."}
            </p>
          </div>
          {subscription?.plan !== "pro" && (
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="rounded-full bg-primary hover:bg-blue-700 gap-2 text-sm"
              data-testid="upgrade-btn"
            >
              {upgrading ? "Redirection..." : "Passer a Pro - 29EUR/mois"}
              <ExternalLink size={14} />
            </Button>
          )}
          {subscription?.plan === "pro" && (
            <div className="flex items-center gap-2 text-green-600">
              <Check size={18} />
              <span className="text-sm font-medium">Actif</span>
            </div>
          )}
        </div>
      </div>

      {/* Shop Info */}
      <div className="bg-white rounded-xl border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Store size={20} className="text-primary" />
          <h2 className="font-semibold text-base">Informations du salon</h2>
        </div>
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">Nom</Label>
              <Input value={shopForm.name} onChange={e => setShopForm({ ...shopForm, name: e.target.value })} className="h-11" data-testid="settings-name" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Telephone</Label>
              <Input value={shopForm.phone} onChange={e => setShopForm({ ...shopForm, phone: e.target.value })} className="h-11" data-testid="settings-phone" />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Adresse</Label>
            <Input value={shopForm.address} onChange={e => setShopForm({ ...shopForm, address: e.target.value })} className="h-11" data-testid="settings-address" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Description</Label>
            <Input value={shopForm.description} onChange={e => setShopForm({ ...shopForm, description: e.target.value })} className="h-11" data-testid="settings-description" />
          </div>
        </div>
      </div>

      {/* Working Hours */}
      <div className="bg-white rounded-xl border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock size={20} className="text-primary" />
          <h2 className="font-semibold text-base">Horaires d'ouverture</h2>
        </div>
        <div className="space-y-3">
          {Object.entries(hours).map(([day, h]) => (
            <div key={day} className="flex items-center gap-4">
              <div className="w-20">
                <p className={`text-sm font-medium ${h.closed ? "text-muted-foreground line-through" : ""}`}>
                  {dayLabels[day] || day}
                </p>
              </div>
              <Switch checked={!h.closed} onCheckedChange={() => toggleDayClosed(day)} />
              {!h.closed && (
                <div className="flex items-center gap-2 text-sm">
                  <input type="time" value={h.open} onChange={e => updateHours(day, "open", e.target.value)} className="border border-border rounded-md px-2 py-1 text-sm" />
                  <span className="text-muted-foreground">-</span>
                  <input type="time" value={h.close} onChange={e => updateHours(day, "close", e.target.value)} className="border border-border rounded-md px-2 py-1 text-sm" />
                </div>
              )}
              {h.closed && <span className="text-xs text-muted-foreground">Ferme</span>}
            </div>
          ))}
        </div>
      </div>

      <Button onClick={saveShop} disabled={saving} className="rounded-full px-8 bg-primary hover:bg-blue-700" data-testid="save-settings-btn">
        {saving ? "Sauvegarde..." : "Sauvegarder les modifications"}
      </Button>
    </div>
  );
}
